import React, { useEffect, useState } from 'react';
import Logo from '../../components/common/Logo';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { useNavigate, Link } from 'react-router-dom';
import { Users, Search, Plus, Tag } from 'lucide-react';
import DoctorService from '../../services/doctor.service';
import { UPLOAD_PREFIX } from '../../config/appConfig';

const AdminDoctorListPage = () => {
    const [doctors, setDoctors] = useState([]);
    const [imageError, setImageError] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        loadDoctors();
    }, []);

    const loadDoctors = async () => {
        try {
            // const data = await UserService.getUsersByRole();
            const data = await DoctorService.getAll();
            console.log("Tag amministrativi del primo professionista:", data[0].amministration_tags);
            setDoctors(data);
        } catch (error) {
            console.error('[DEBUG] Error loading doctors:', error);
        }
    };

    const filteredDoctors = doctors.filter(doctor => {
        // Ricerca nel nome, email e specializzazione
        const basicInfoMatch =
            `${doctor.first_name} ${doctor.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doctor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (doctor.specialization && doctor.specialization.toLowerCase().includes(searchTerm.toLowerCase()));

        // Ricerca nei tag normali
        const tagsMatch = doctor.tags && doctor.tags.some(tag =>
            tag.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Ricerca nei tag amministrativi
        const adminTagsMatch = doctor.amministration_tags && doctor.amministration_tags.some(tag =>
            tag.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Ritorna true se trova corrispondenze in qualsiasi campo
        return basicInfoMatch || tagsMatch || adminTagsMatch;
    });

    return (
        <div className="flex min-h-screen">
            <div className="w-64 bg-white shadow-md">
                <div className="p-4">
                    <Logo />
                </div>
                <AdminSidebar />
            </div>

            <div className="flex-1 bg-gray-50 p-8">
                <header className="mb-8">
                    <div className="flex justify-between items-center">
                        <h1 className="text-2xl font-bold">Gestione Professionisti</h1>
                        <Link
                            to="/admin/doctors/create"
                            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Nuovo Professionista
                        </Link>
                    </div>

                    <div className="mt-4 relative">
                        <input
                            type="text"
                            placeholder="Cerca per nome, email, specializzazione o tag..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full p-3 pl-10 border rounded-lg"
                        />
                        <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                </header>

                {filteredDoctors.length === 0 ? (
                    <div className="bg-white p-8 rounded-lg shadow text-center">
                        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            Nessun Professionista trovato
                        </h3>
                        <p className="text-gray-500">
                            {searchTerm
                                ? "Nessun professionista corrisponde ai criteri di ricerca"
                                : "Non ci sono dottori registrati nel sistema"}
                        </p>
                    </div>
                ) : (

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">

                        {filteredDoctors.map((doctor) => (
                            <div
                                key={doctor.id}
                                className="bg-white rounded-lg shadow hover:shadow-md transition-all cursor-pointer overflow-hidden"
                                onClick={() => navigate(`/admin/doctors/${doctor.id}`)}
                            >
                                {/* Sezione immagine a larghezza piena */}
                                <div className="w-full h-64 relative">
                                    {doctor.profile_picture ? (
                                        <img
                                            src={`${UPLOAD_PREFIX}/storage/${doctor.profile_picture}`}
                                            alt={`${doctor.first_name} ${doctor.last_name}`}
                                            className="w-full h-full object-cover object-top"
                                            onError={() => setImageError(true)}

                                        />
                                    ) : (
                                        <div className="w-full h-full bg-purple-100 flex items-center justify-center">
                                            <span className="text-purple-600 font-semibold text-4xl">
                                                {doctor.first_name.charAt(0)}{doctor.last_name.charAt(0)}
                                            </span>
                                        </div>
                                    )}

                                    {/* Indicatore di stato */}
                                    <div className={`absolute top-3 right-3 w-4 h-4 rounded-full ${doctor.login_enabled ? 'bg-green-500' : 'bg-red-500'} border-2 border-white`} />
                                </div>

                                {/* Contenuto testuale */}
                                <div className="p-4">
                                    <h2 className="font-semibold text-lg">
                                        {doctor.first_name} {doctor.last_name}
                                    </h2>
                                    <p className="text-sm text-gray-600">{doctor.email}</p>
                                    <p className="text-sm text-gray-600 mb-2">{doctor.phone}</p>

                                    {doctor.specialization && (
                                        <p className="text-sm text-purple-600 font-medium mt-2">
                                            Specializzazione: {doctor.specialization}
                                        </p>
                                    )}

                                    {/* Sezione dei tag */}
                                    {doctor.tags && doctor.tags.length > 0 && (
                                        <div className="mt-3">
                                            <p className="text-xs text-gray-500 mb-1">Aree di specializzazione:</p>
                                            <div className="flex flex-wrap gap-1">
                                                {doctor.tags.map(tag => (
                                                    <span
                                                        key={tag.id}
                                                        className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full"
                                                    >
                                                        {tag.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Sezione delle preferenze orarie */}
                                    {doctor.time_preferences && (
                                        <div className="mt-3">
                                            <p className="text-xs text-gray-500 mb-1">Disponibilità:</p>
                                            <div className="flex flex-wrap gap-1">
                                                {doctor.time_preferences.morning && (
                                                    <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                                        Mattina
                                                    </span>
                                                )}
                                                {doctor.time_preferences.afternoon && (
                                                    <span className="inline-block bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                                                        Pomeriggio
                                                    </span>
                                                )}
                                                {doctor.time_preferences.evening && (
                                                    <span className="inline-block bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded-full">
                                                        Sera
                                                    </span>
                                                )}
                                                {doctor.time_preferences.flexible && (
                                                    <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                                        Flessibile
                                                    </span>
                                                )}
                                                {!doctor.time_preferences.morning &&
                                                    !doctor.time_preferences.afternoon &&
                                                    !doctor.time_preferences.evening &&
                                                    !doctor.time_preferences.flexible && (
                                                        <span className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
                                                            Non specificata
                                                        </span>
                                                    )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Sezione dei tag amministrativi */}
                                    {doctor.amministration_tags && doctor.amministration_tags.length > 0 && (
                                        <div className="mt-3">
                                            <p className="text-xs text-gray-500 mb-1 flex items-center">
                                                <Tag className="w-3 h-3 mr-1" />
                                                Tag Amministrativi:
                                            </p>
                                            <div className="flex flex-wrap gap-1">
                                                {doctor.amministration_tags.map(tag => (
                                                    <span
                                                        key={`admin-tag-${tag.id}`}
                                                        className="inline-block text-xs px-2 py-1 rounded-full"
                                                        style={{
                                                            backgroundColor: `${tag.color}20`,
                                                            color: tag.color,
                                                            borderColor: tag.color,
                                                            borderWidth: '1px'
                                                        }}
                                                    >
                                                        {tag.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}


                    </div>
                )}


            </div>
        </div>
    );
};

export default AdminDoctorListPage;